import React, { useEffect } from 'react';
import { Box, Card, Text, Title, Stack, Group, Button, Badge, Skeleton, ActionIcon, Tooltip, Divider, Flex, Paper } from '@mantine/core';
import { HiOutlineDeviceMobile, HiOutlineDesktopComputer, HiOutlineTrash, HiOutlineShieldCheck, HiOutlineGlobeAlt, HiOutlineCheckCircle } from 'react-icons/hi';
import { useAuthStore } from '../store/useAuthStore';
import dayjs from 'dayjs';

const UserSessions = () => {
  const { sessions, loadingSessions, fetchSessions, closeSession, closeOtherSessions, username } = useAuthStore();

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Ordenamos para que la sesión actual aparezca PRIMERA
  const sortedSessions = [...sessions].sort((a, b) => (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0));

  return (
    <Box className="w-full pt-2 px-2">
      <Flex justify="space-between" align="center" mb="lg" px="md">
        <Box>
          <Title order={1} c="dark.5">Seguridad de la Cuenta</Title>
          <Text c="dimmed" size="sm" fw={500}>Administra tus sesiones activas y dispositivos conectados</Text>
        </Box>
        
        <Button 
          variant="light" 
          color="red" 
          leftSection={<HiOutlineTrash />}
          onClick={closeOtherSessions}
          disabled={sessions.length <= 1}
        >
          Cerrar las demás sesiones
        </Button>
      </Flex>

      <Stack px="md" gap="md">
        <Card withBorder radius="lg" p="xl" shadow="sm">
          <Group mb="xl">
            <HiOutlineShieldCheck size={24} className="text-yellow-600" />
            <Box>
              <Text fw={800} size="lg" tt="uppercase" className="tracking-widest">Sesiones Activas</Text>
              <Text size="xs" c="dimmed">Has iniciado sesión en estos dispositivos</Text>
            </Box>
          </Group>

          <Stack gap="md">
            {loadingSessions ? (
              <LoadingSkeletons />
            ) : (
              sortedSessions.map((session, index) => (
                <SessionItem 
                  key={session._id} 
                  session={session} 
                  onClose={() => closeSession(session._id)}
                  isLast={index === sortedSessions.length - 1}
                />
              ))
            )}
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
};

const SessionItem = ({ session, onClose, isLast }) => {
  const isMobile = /mobile/i.test(session.userAgent || '');
  
  return (
    <Paper 
      withBorder={session.isCurrent} 
      shadow={session.isCurrent ? "md" : "none"}
      p={session.isCurrent ? "md" : "xs"}
      radius="lg"
      className={`transition-all duration-300 ${session.isCurrent ? 'bg-green-50/40 border-green-400' : 'hover:bg-gray-50/50'}`}
      style={session.isCurrent ? { borderLeft: '6px solid var(--mantine-color-green-6)' } : {}}
    >
      <Flex justify="space-between" align="center">
        <Group gap="lg">
          <ActionIcon 
            variant={session.isCurrent ? "filled" : "light"} 
            color={session.isCurrent ? "green" : "gray"} 
            size="xl"
            radius="md"
          >
            {isMobile ? <HiOutlineDeviceMobile size={24} /> : <HiOutlineDesktopComputer size={24} />}
          </ActionIcon>
          
          <Box>
            <Group gap="xs">
              <Text fw={session.isCurrent ? 800 : 700} size="sm" c={session.isCurrent ? "green.9" : "dark.4"}>
                {isMobile ? 'Dispositivo Móvil' : 'Navegador Web'}
              </Text>
              {session.isCurrent && (
                <Badge 
                  color="green" 
                  variant="filled" 
                  size="sm" 
                  radius="sm"
                  leftSection={<HiOutlineCheckCircle size={12} />}
                  className="animate-pulse"
                >
                  ESTE DISPOSITIVO
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed" className="max-w-xs truncate" fw={session.isCurrent ? 600 : 400}>
              {session.userAgent || 'Desconocido'}
            </Text>
            <Group gap="xs" mt={4}>
              <Flex align="center" gap={4}>
                <HiOutlineGlobeAlt size={12} className={session.isCurrent ? "text-green-600" : "text-gray-400"} />
                <Text size="xs" c="dimmed" fw={session.isCurrent ? 600 : 400}>{session.ip || '0.0.0.0'}</Text>
              </Flex>
              <Text size="xs" c="dimmed">•</Text>
              <Text size="xs" c="dimmed" fw={session.isCurrent ? 600 : 400}>Iniciada el {dayjs(session.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
            </Group>
          </Box>
        </Group>

        {!session.isCurrent && (
          <Tooltip label="Cerrar esta sesión" position="left" withArrow fontSize="xs">
            <ActionIcon variant="subtle" color="red" size="lg" onClick={onClose} radius="md">
              <HiOutlineTrash size={20} />
            </ActionIcon>
          </Tooltip>
        )}
      </Flex>
      {!isLast && !session.isCurrent && <Divider variant="dashed" mt="md" />}
    </Paper>
  );
};

const LoadingSkeletons = () => (
  <Stack>
    {[1, 2, 3].map(i => <Skeleton key={i} height={60} radius="md" />)}
  </Stack>
);

export default UserSessions;
